'use client';

import { c as _c } from "react/compiler-runtime";
import { TRPC_SETTINGS } from '~/constants/trpc';
import { useTripId } from '~/hooks/useTripId';
import { api } from '~/libs/api';
import type { TripOverviewLeg, TripOverviewStay } from '~/services/engine-ts';
import { useTripBookingStore } from '~/stores/tpv2/tripBookingStore';
import { formatTripDateRange } from '~/utils/dateHelpers/dateHelpers';
import { aggregatePax } from '~/utils/paxHelpers/paxHelper';
import { deriveTripEditability } from '../lib/tripEditability';
import { useTripLiveCheck } from './useTripLiveCheck';
export const useTripOverviewSummary = () => {
  const $ = _c(35);
  const tripId = useTripId();
  let t0;
  if ($[0] !== tripId) {
    t0 = {
      tripId
    };
    $[0] = tripId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = !!tripId;
  let t2;
  if ($[2] !== t1) {
    t2 = {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH,
      enabled: t1,
      keepPreviousData: true
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: overviewData
  } = api.trip.getOverview.useQuery(t0, t2);
  let t3;
  if ($[4] !== overviewData?.items) {
    t3 = overviewData?.items.find(_temp);
    $[4] = overviewData?.items;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const firstLeg = t3;
  const firstItem = overviewData?.items.find(_temp2);
  const lastItem = overviewData?.items.find(_temp3);
  const originCity = firstLeg?.origin.name ?? "";
  let t4;
  if ($[6] !== overviewData) {
    t4 = deriveTripEditability(overviewData);
    $[6] = overviewData;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    isDateUnset,
    isReadOnly,
    canEditTripSelections
  } = t4;
  const isLiveChecking = useTripLiveCheck(tripId, overviewData?.priceStale === true);
  const confirmedTripId = useTripBookingStore(_temp4);
  let t5;
  if ($[8] !== confirmedTripId || $[9] !== overviewData?.previousIds || $[10] !== tripId) {
    t5 = confirmedTripId !== null && (confirmedTripId === tripId || (overviewData?.previousIds ?? []).includes(confirmedTripId));
    $[8] = confirmedTripId;
    $[9] = overviewData?.previousIds;
    $[10] = tripId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const datesConfirmed = t5;
  const dateRange = isDateUnset ? "" : formatTripDateRange(firstItem?.startDate ?? "", lastItem?.endDate ?? "");
  let t6;
  if ($[12] !== overviewData?.pax.travellerMixes) {
    t6 = overviewData?.pax.travellerMixes ?? [];
    $[12] = overviewData?.pax.travellerMixes;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = aggregatePax(t6);
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const {
    adultCount,
    childAges
  } = t7;
  const travellerCount = adultCount + childAges.length;
  const rooms = overviewData?.pax.numberOfRooms ?? 1;
  let t8;
  if ($[16] !== overviewData?.items) {
    t8 = overviewData?.items.filter(_temp5).reduce(_temp6, 0) ?? 0;
    $[16] = overviewData?.items;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const nights = t8;
  let t9;
  if ($[18] !== adultCount || $[19] !== canEditTripSelections || $[20] !== childAges || $[21] !== dateRange || $[22] !== datesConfirmed || $[23] !== firstItem || $[24] !== isDateUnset || $[25] !== isLiveChecking || $[26] !== isReadOnly || $[27] !== lastItem || $[28] !== nights || $[29] !== originCity || $[30] !== overviewData || $[31] !== rooms || $[32] !== travellerCount || $[33] !== tripId) {
    t9 = {
      tripId,
      overviewData,
      firstItem,
      lastItem,
      originCity,
      dateRange,
      travellerCount,
      adultCount,
      childAges,
      rooms,
      nights,
      isReadOnly,
      isDateUnset,
      canEditTripSelections,
      datesConfirmed,
      isLiveChecking
    };
    $[18] = adultCount;
    $[19] = canEditTripSelections;
    $[20] = childAges;
    $[21] = dateRange;
    $[22] = datesConfirmed;
    $[23] = firstItem;
    $[24] = isDateUnset;
    $[25] = isLiveChecking;
    $[26] = isReadOnly;
    $[27] = lastItem;
    $[28] = nights;
    $[29] = originCity;
    $[30] = overviewData;
    $[31] = rooms;
    $[32] = travellerCount;
    $[33] = tripId;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  return t9;
};
function _temp(i) {
  return i.type === "leg";
}
function _temp2(i_0) {
  return i_0.isFirstItem;
}
function _temp3(i_1) {
  return i_1.isLastItem;
}
function _temp4(s) {
  return s.datesConfirmedTripId;
}
function _temp5(i_2) {
  return i_2.type === "stay";
}
function _temp6(sum, i_3) {
  return sum + (i_3 as TripOverviewStay).nights;
}